import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import * as React from "react";
import * as uuid from "uuid";
import { DriversMenuRow, DriversRow } from "../../api";
import { DriverPulldown } from "../../components/driverPulldown";
import { OfficePullDown } from "../../components/officePullDown";
import {
    LoadingTableElement,
    NullTableElement,
} from "../../components/table/nullTable";
import {
    HeadCell,
    SortableTable,
    newDisplayCond,
} from "../../components/table/sortableTable";
import * as Service from "../../services/driver_list_menu/select";
import { commonState } from "../../store";
import { colors } from "../../theme";
import { PlanTypePulldown } from "./PlanTypePulldown";
import { DriverMenuState } from "./store";

interface Props {
    common: commonState;
    drivermenu: DriverMenuState;
    selectedOfficeId?: number;
}

const SearchBar: React.FC<Props> = (props: Props) => {
    const onSelectDriver = (driver: DriversRow) => {
        Service.selectDriver(driver);
    };

    const onClearDriver = () => {
        Service.selectDriver(null);
    };

    const onSelectOffice = (office_id: number | null) => {
        Service.selectOfficeId(office_id);
    };

    const onSelectPlanType = (type: string | null) => {
        console.log("Changing selected plan type", type);
        Service.selectPlanType(type);
    };

    const clickSearch = () => {
        Service.updateDriverList();
    };

    return (
        <Box sx={{ display: "flex", width: "100%", mb: 7 }}>
            <Box sx={{ display: "flex", alignItems: "end" }}>
                <Box sx={{ display: "inline-flex", ml: 4, mr: 4 }}>
                    <DriverPulldown
                        selectedDriver={props.drivermenu.selectedDriver}
                        onChange={onSelectDriver}
                        onClear={onClearDriver}
                        driverCompleteData={props.common.driverNames}
                        initWithNull={false}
                    />
                </Box>
                <Box sx={{ ml: 4, mr: 4 }}>
                    <OfficePullDown
                        useAllOfficeIds={false}
                        selectedOfficeId={props.drivermenu.selectedOfficeId}
                        onChange={onSelectOffice}
                    />
                </Box>
                <PlanTypePulldown
                    selectedPlanType={props.drivermenu.selectedPlanType}
                    onChange={onSelectPlanType}
                />

                <Box sx={{ ml: 4, mb: 1 }}>
                    <Button
                        variant="contained"
                        onClick={clickSearch}
                        color="primary"
                        size="small"
                    >
                        検索
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

const convertResultsToTable = (
    drivers_menu_list?: DriversMenuRow[] | null
): Row[] => {
    if (!drivers_menu_list) {
        return [];
    }

    const _data = drivers_menu_list.map((rec: DriversMenuRow) => {
        return {
            office_name: rec.officeName,
            plan_type: rec.planType,
            driver_code: rec.driverCode,
            driver_name: rec.driverName,
            is_retired: rec.isRetired,
            row_id: uuid.v4(),
        } as Row;
    });

    return _data;
};

const Table: React.FC<Props> = (props: Props) => {
    const _data = convertResultsToTable(props.drivermenu.results);

    if (!props.drivermenu.results) {
        return (
            <Box id="DriversListTable-container">
                <LoadingTableElement />
            </Box>
        );
    }

    if (props.drivermenu.results.length == 0) {
        return (
            <Box id="DriversListTable-container">
                <NullTableElement />
            </Box>
        );
    }

    return (
        <Box
            id="DriversListTable-container"
            sx={{ display: "flex", justifyContent: "center" }}
        >
            <Box sx={{ width: "100%" }}>
                <SortableTable
                    tableHeaders={headCells}
                    tableData={_data}
                    tableProps={props}
                    displayCond={newDisplayCond("driver_id", "asc", 0, 20)}
                    rowIdField={"row_id"}
                    onRowClickMethod={() => {}}
                    highlightOnHover={false}
                    changeRowColorOnSelect={false}
                    // slimRows={true}
                />
            </Box>
        </Box>
    );
};

export const DriversListContainer: React.FC<Props> = (props: Props) => {
    return (
        <Box>
            <SearchBar {...props} />
            <Table {...props} />
        </Box>
    );
};

export const headCells: HeadCell[] = [
    {
        label: "営業所",
        id: "office_name",
        numeric: false,
        grow: 3,
        cell: function Cell(r) {
            let style = { color: "inherit" };
            if (r.is_retired) {
                style = { color: colors.red.main };
            }
            return <Box sx={style}>{r.office_name}</Box>;
        },
    },
    {
        label: "契約種別",
        id: "plan_type",
        numeric: false,
        grow: 1,
        cell: function Cell(r) {
            let style = { color: "inherit" };
            if (r.is_retired) {
                style = { color: colors.red.main };
            }
            return <Box sx={style}>{r.plan_type}</Box>;
        },
    },
    {
        label: "乗務員番号",
        id: "driver_code",
        numeric: false,
        headerAlignRight: false,
        grow: 2,
        cell: function Cell(r) {
            let style = { color: "inherit" };
            if (r.is_retired) {
                style = { color: colors.red.main };
            }
            return <Box sx={style}>{r.driver_code}</Box>;
        },
    },
    {
        label: "乗務員氏名",
        id: "driver_name",
        numeric: false,
        grow: 3,
        cell: function Cell(r) {
            let style = { color: "inherit" };
            if (r.is_retired) {
                style = { color: colors.red.main };
            }
            return <Box sx={style}>{r.driver_name}</Box>;
        },
    },
    {
        label: "退職者",
        id: "is_retired",
        numeric: false,
        grow: 1,
        cell: function Cell(r) {
            let style = { color: "inherit" };
            let flag = "";
            if (r.is_retired) {
                style = { color: colors.red.main };
                flag = "✔︎";
            }
            return <Box sx={style}>{flag}</Box>;
        },
    },
];

type Row = {
    office_name?: string;
    plan_type?: string;
    driver_code?: string;
    driver_name?: string;
    is_retired?: boolean;
};
